'use client';

import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);
};

export default function OurMissionPage() {
  useScrollReveal();

  return (
    <div className="bg-white">
      {/* HERO IMAGE HEADER */}
      <section className="relative h-[60vh] min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/svetlana-gumerova-gOLCAOuc7iA-unsplash-1779197330178.jpg"
            alt="Lisbon iconic 25 de Abril bridge spanning the Tagus river at sunset"
            fill
            className="object-cover object-center"
            priority
            quality={90}
          />
          <div className="absolute inset-0 city-overlay" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 pb-16 pt-32">
          <p className="eyebrow text-white/80 mb-4">Our Mission</p>
          <h1 className="font-headline font-extrabold text-white" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            We Help Students Build<br />Their Future in Europe
          </h1>
        </div>
      </section>
      {/* MISSION BODY */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="fade-in-up">
            <span className="accent-bar" />
            <p className="font-body leading-relaxed mb-6" style={{ fontSize: '1.2rem', color: '#3d5566' }}>
              At Nexus Vantage, we believe every ambitious student deserves a clear, supported path to world-class education. We are a specialist education and immigration consultancy based in Lisbon, Portugal, dedicated to helping students from India and the GCC study in Spain and Portugal.
            </p>
            <p className="font-body leading-relaxed mb-6" style={{ fontSize: '1.2rem', color: '#3d5566' }}>
              Whether you are exploring your options for the first time or ready to apply, our consultants are here to guide you — personally, transparently, and without unnecessary complexity.
            </p>
            <p className="font-body leading-relaxed" style={{ fontSize: '1.2rem', color: '#3d5566' }}>
              Spain and Portugal offer world-ranked universities, tuition starting from €700 per year, a 2-year post-study work visa, and a direct pathway to EU residency. We are here to make that opportunity real for you.
            </p>
          </div>
        </div>
      </section>
      {/* VALUE PILLARS */}
      <section className="py-20" style={{ backgroundColor: '#F5EDD6' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 fade-in-up">
            <span className="eyebrow">Our Values</span>
            <h2 className="font-headline font-bold mt-3" style={{ color: '#1a2e3b' }}>
              How We Work With You
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                ),
                title: 'Personalised Guidance',
                desc: 'Our consultants take the time to understand your background, goals, and budget before recommending any university or programme',
              },
              {
                icon: (
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: 'Clear and Honest Process',
                desc: 'No hidden fees, no confusing jargon. We walk you through every requirement, document, and deadline in plain language',
              },
              {
                icon: (
                  <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                ),
                title: 'Support That Doesn\'t Stop',
                desc: 'We stay with you from your first enquiry through your university application, visa approval, arrival, and life in Europe',
              },
            ]?.map((pillar, i) => (
              <div
                key={i}
                className="fade-in-up hover-lift bg-white rounded-2xl p-8 border"
                style={{ transitionDelay: `${i * 0.12}s`, borderColor: '#e0d8c8' }}
              >
                <div className="mb-6" style={{ color: '#2A7FA5' }}>
                  {pillar?.icon}
                </div>
                <h3 className="font-headline font-bold mb-4" style={{ fontSize: '1.35rem', color: '#1a2e3b' }}>
                  {pillar?.title}
                </h3>
                <p className="font-body text-base leading-relaxed" style={{ color: '#3d5566' }}>
                  {pillar?.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* STATS ROW */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '100+', label: 'Iberian Universities' },
              { number: '€800', label: 'Avg. Monthly Cost' },
              { number: '5 Years', label: 'to EU Residency' },
              { number: '27', label: 'EU Member States Access' },
            ]?.map((stat, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div
                  className="font-headline font-extrabold mb-2"
                  style={{ fontSize: 'clamp(2.2rem, 4vw, 3rem)', color: '#2A7FA5', lineHeight: 1 }}
                >
                  {stat?.number}
                </div>
                <div className="font-body font-medium text-sm uppercase tracking-wider" style={{ color: '#3d5566' }}>
                  {stat?.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: '#2A7FA5' }}>
        <div className="max-w-3xl mx-auto px-6 text-center fade-in-up">
          <h2 className="font-headline font-bold text-white mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)' }}>
            Ready to Start Your Journey?
          </h2>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-body font-semibold rounded-lg transition-all duration-300 hover:-translate-y-1"
            style={{ backgroundColor: '#F5EDD6', color: '#2A7FA5', fontSize: '1.05rem' }}
          >
            Book a Free Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
